import React, { useState, useEffect } from "react";
import { Card, Typography, Pagination, Table, Tag, DatePicker, Select, Button, Form, message, Row, Col, Space, Input, Popconfirm, Modal } from "antd";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { EditTwoTone, CheckCircleTwoTone, CloseCircleTwoTone, TeamOutlined, PlusOutlined, SearchOutlined, DeleteOutlined } from "@ant-design/icons";
import moment from "moment";
import dayjs from "dayjs";
import { capitalizeWords } from "../../../../components/icons/ApplicationColor";
import CommonTopDetails from "../../../../components/AdminComponents/CommonTopDetails";
import useCommonListing from "../../../../components/AdminComponents/customHooks/useCommonListing";
import axiosInstance from "../../../../api/Axios";

const { Title } = Typography;
const { Option } = Select;

const getCookie = (name) => {
  const cookies = document.cookie.split("; ");
  for (let i = 0; i < cookies.length; i++) {
    const [cookieName, cookieValue] = cookies[i].split("=");
    if (cookieName === name) return cookieValue;
  }
  return null;
};

const ProjectEditRow = ({ record, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    form.setFieldsValue({
      cust_access_start_date: record.cust_access_start_date ? dayjs(record.cust_access_start_date) : null,
      cust_access_end_date: record.cust_access_end_date ? dayjs(record.cust_access_end_date) : null,
      customer_access: record.customer_access ? "active" : "inactive",
    });
  }, [record, form]);

  const handleFinish = async (values) => {
    setSaving(true);
    try {
      const payload = {
        id: String(record.id),
        cust_access_start_date: values.cust_access_start_date ? values.cust_access_start_date.format("YYYY-MM-DD") : null,
        cust_access_end_date: values.cust_access_end_date ? values.cust_access_end_date.format("YYYY-MM-DD") : null,
        customer_access: values.customer_access === "active",
      };

      const csrfToken = getCookie("csrftoken");

      await axiosInstance.post("/customer/project/access/", payload, {
        headers: {
          "X-CSRFToken": csrfToken,
        },
      });

      message.success("Project access configuration updated successfully.");
      onSuccess();
    } catch (error) {
      console.error("Error updating project access configuration:", error);
      message.error("Failed to update project access configuration.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        padding: "10px 20px",
        background: "#f4f7fa",
        borderLeft: "4px solid #1890ff",
        borderRadius: "0 8px 8px 0",
        margin: "4px 8px",
        boxShadow: "inset 0 1px 3px rgba(0,0,0,0.05)",
        overflow: "hidden",
        animation: "slideDown 0.35s cubic-bezier(0.4, 0, 0.2, 1) forwards",
      }}
    >
      <style>
        {`
          @keyframes slideDown {
            from {
              opacity: 0;
              max-height: 0;
              padding-top: 0;
              padding-bottom: 0;
              transform: translateY(-8px);
            }
            to {
              opacity: 1;
              max-height: 300px;
              padding-top: 10px;
              padding-bottom: 10px;
              transform: translateY(0);
            }
          }
        `}
      </style>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        requiredMark={false}
      >
        <Row gutter={24} align="bottom">
          <Col xs={24} sm={8}>
            <Form.Item
              label="Access Start Date"
              name="cust_access_start_date"
              rules={[{ required: true, message: "Start date is required" }]}
            >
              <DatePicker style={{ width: "100%" }} format="MMM DD YYYY" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              label="Access End Date"
              name="cust_access_end_date"
              rules={[{ required: true, message: "End date is required" }]}
            >
              <DatePicker style={{ width: "100%" }} format="MMM DD YYYY" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              label="Login Access Status"
              name="customer_access"
              rules={[{ required: true, message: "Status is required" }]}
            >
              <Select style={{ width: "100%" }}>
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row justify="end" style={{ marginTop: "16px" }}>
          <Space>
            <Button onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={saving} style={{ background: "#254061", borderColor: "#254061" }}>
              Update
            </Button>
          </Space>
        </Row>
      </Form>
    </div>
  );
};

const ProjectUsersRow = ({ record, customerId }) => {
  const [loadAgain, setLoadAgain] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [unassignedLoadAgain, setUnassignedLoadAgain] = useState(false);

  const {
    data: users,
    itemsLength,
    loading,
    filterData,
    onShowSizeChange,
    onPageChange,
    handleSearchChange,
  } = useCommonListing({
    passSize: 5,
    passPage: 1,
    passURL: `/customer/projects/users/?customer_id=${customerId}&project_id=${record.id}`,
    includeStatus: false,
    loadAgain: loadAgain,
  });

  const {
    data: unassignedUsers,
    itemsLength: unassignedItemsLength,
    loading: unassignedLoading,
    filterData: unassignedFilterData,
    onShowSizeChange: unassignedOnShowSizeChange,
    onPageChange: unassignedOnPageChange,
    handleSearchChange: unassignedHandleSearchChange,
  } = useCommonListing({
    passSize: 5,
    passPage: 1,
    passURL: `/customer/projects/users/notassigned/?customer_id=${customerId}&project_id=${record.id}`,
    includeStatus: false,
    loadAgain: unassignedLoadAgain,
    skip: !isModalOpen,
  });

  const handleDeleteUser = async (userRecord) => {
    try {
      const csrfToken = getCookie("csrftoken");
      await axiosInstance.delete(
        `/customer/projects/users/remove/?customer_id=${customerId}&project_id=${record.id}&user_id=${userRecord.id}`,
        {
          headers: {
            "X-CSRFToken": csrfToken,
          },
        }
      );
      message.success("User removed from project successfully.");
      setLoadAgain((prev) => !prev);
    } catch (error) {
      console.error("Error removing user from project:", error);
      message.error("Failed to remove user from project.");
    }
  };

  const cellStyle = { paddingLeft: "8px", fontSize: "13px" };
  const headerStyle = { paddingLeft: "8px", fontSize: "13px", fontWeight: 500 };

  const userColumns = [
    {
      title: <div style={headerStyle}>User ID</div>,
      dataIndex: "user_id",
      key: "user_id",
      width: 100,
      render: (text) => <div style={cellStyle}>{text || "-"}</div>,
    },
    {
      title: <div style={headerStyle}>First Name</div>,
      dataIndex: "first_name",
      key: "first_name",
      width: 90,
      render: (text) => <div style={cellStyle}>{text || "-"}</div>,
    },
    {
      title: <div style={headerStyle}>Last Name</div>,
      dataIndex: "last_name",
      key: "last_name",
      width: 90,
      render: (text) => <div style={cellStyle}>{text || "-"}</div>,
    },
    {
      title: <div style={headerStyle}>User Role</div>,
      dataIndex: "user_role",
      key: "user_role",
      width: 90,
      render: (role) => (
        <div style={cellStyle}>
          {role ? capitalizeWords(role) : "-"}
        </div>
      ),
    },
    {
      title: <div style={headerStyle}>Email</div>,
      dataIndex: "email",
      key: "email",
      width: 180,
      ellipsis: true,
      render: (text) => <div style={cellStyle}>{text || "-"}</div>,
    },
    {
      title: <div style={headerStyle}>Access End Date</div>,
      dataIndex: "access_end_date",
      key: "access_end_date",
      width: 110,
      render: (date) => (
        <div style={cellStyle}>
          {date ? moment(date).format("MMM-DD-YYYY") : "-"}
        </div>
      ),
    },
    {
      title: <div style={headerStyle}>Creation Date</div>,
      dataIndex: "created_at",
      key: "created_at",
      width: 110,
      render: (date) => (
        <div style={cellStyle}>
          {date ? moment(date).format("MMM-DD-YYYY") : "-"}
        </div>
      ),
    },
    {
      title: <div style={headerStyle}>Last Update</div>,
      dataIndex: "updated_at",
      key: "updated_at",
      width: 110,
      render: (date) => (
        <div style={cellStyle}>
          {date ? moment(date).format("MMM-DD-YYYY") : "-"}
        </div>
      ),
    },
    {
      title: <div style={headerStyle}>Action</div>,
      key: "action",
      width: 70,
      align: "center",
      fixed: "right",
      render: (_, userRecord) => (
        <Popconfirm
          title="Are you sure you want to remove this user from this project?"
          onConfirm={() => handleDeleteUser(userRecord)}
          okText="Yes"
          cancelText="No"
        >
          <Button
            type="text"
            danger
            icon={<DeleteOutlined style={{ fontSize: "13px" }} />}
            size="small"
          />
        </Popconfirm>
      ),
    },
  ];

  const handleAddUser = async (userRecord) => {
    try {
      const csrfToken = getCookie("csrftoken");
      await axiosInstance.post(
        `/customer/projects/users/assign/`,
        {
          customer_id: customerId,
          project_id: record.id,
          user_id: userRecord.id,
        },
        {
          headers: {
            "X-CSRFToken": csrfToken,
          },
        }
      );
      message.success("User added to project successfully.");
      setUnassignedLoadAgain((prev) => !prev);
      setLoadAgain((prev) => !prev);
    } catch (error) {
      console.error("Error adding user to project:", error);
      message.error("Failed to add user to project. Ensure the API endpoint is correct.");
    }
  };

  const unassignedUserColumns = [
    {
      title: <div style={headerStyle}>User ID</div>,
      dataIndex: "user_id",
      key: "user_id",
      width: 100,
      render: (text) => <div style={cellStyle}>{text || "-"}</div>,
    },
    {
      title: <div style={headerStyle}>First Name</div>,
      dataIndex: "first_name",
      key: "first_name",
      width: 100,
      render: (text) => <div style={cellStyle}>{text || "-"}</div>,
    },
    {
      title: <div style={headerStyle}>Last Name</div>,
      dataIndex: "last_name",
      key: "last_name",
      width: 100,
      render: (text) => <div style={cellStyle}>{text || "-"}</div>,
    },
    {
      title: <div style={headerStyle}>Email</div>,
      dataIndex: "email",
      key: "email",
      width: 180,
      ellipsis: true,
      render: (text) => <div style={cellStyle}>{text || "-"}</div>,
    },
    {
      title: <div style={headerStyle}>Action</div>,
      key: "action",
      width: 80,
      align: "center",
      render: (_, userRecord) => (
        <Button
          type="primary"
          size="small"
          onClick={() => handleAddUser(userRecord)}
          style={{ background: "#254061", borderColor: "#254061" }}
        >
          Add
        </Button>
      ),
    },
  ];

  return (
    <div
      style={{
        padding: "16px 20px",
        background: "#f8fafd",
        borderLeft: "4px solid #254061",
        borderRadius: "0 8px 8px 0",
        margin: "4px 8px",
        boxShadow: "inset 0 1px 3px rgba(0,0,0,0.05)",
        overflow: "hidden",
        animation: "slideDown 0.35s cubic-bezier(0.4, 0, 0.2, 1) forwards",
      }}
    >
      <style>
        {`
          @keyframes slideDown {
            from {
              opacity: 0;
              max-height: 0;
              padding-top: 0;
              padding-bottom: 0;
              transform: translateY(-8px);
            }
            to {
              opacity: 1;
              max-height: 800px;
              padding-top: 16px;
              padding-bottom: 16px;
              transform: translateY(0);
            }
          }
        `}
      </style>
      
      {/* Header section matching the screenshot */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
          <span style={{ fontSize: "16px", fontWeight: "600", color: "#254061" }}>Users</span>
          <span style={{ color: "#8c8c8c", fontSize: "14px" }}>| {itemsLength || 0} user(s)</span>
        </div>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
           <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{
              background: "#254061",
              borderColor: "#254061",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "32px",
              width: "32px",
              borderRadius: "4px",
              padding: 0,
            }}
            onClick={() => {
              setIsModalOpen(true);
              setUnassignedLoadAgain((prev) => !prev);
            }}
          />
          <Input
            placeholder="Search..."
            onChange={handleSearchChange}
            value={filterData.search}
            suffix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            style={{ width: "200px" }}
          />
        </div>
      </div>

      <Table
        columns={userColumns}
        dataSource={users}
        rowKey="id"
        size="small"
        loading={loading}
        pagination={false}
        bordered
        scroll={{ x: 950 }}
        style={{ fontSize: "12px" }}
      />

      {/* Footer matching screenshot pagination and page sizing */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "12px" }}>
        <div style={{ color: "#595959" }}>
          Show{" "}
          <Select
            size="small"
            value={filterData.pageSize}
            onChange={(val) => onShowSizeChange(1, val)}
            style={{ width: 65 }}
          >
            <Option value={5}>5</Option>
            <Option value={10}>10</Option>
            <Option value={20}>20</Option>
            <Option value={50}>50</Option>
          </Select>{" "}
          entries
        </div>
        <Pagination
          size="small"
          current={filterData.currentPage}
          pageSize={filterData.pageSize}
          total={itemsLength}
          onChange={onPageChange}
          showSizeChanger={false}
        />
      </div>

      <Modal
        title={`Add User to Project: ${record.project_name || record.project_id}`}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <div style={{ color: "#8c8c8c", fontSize: "14px" }}>
            {unassignedItemsLength || 0} user(s) available
          </div>
          <Input
            placeholder="Search users..."
            onChange={unassignedHandleSearchChange}
            value={unassignedFilterData.search}
            suffix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            style={{ width: "250px" }}
          />
        </div>

        <Table
          columns={unassignedUserColumns}
          dataSource={unassignedUsers}
          rowKey="id"
          size="small"
          loading={unassignedLoading}
          pagination={false}
          bordered
          scroll={{ y: 400 }}
          style={{ fontSize: "12px" }}
        />

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "16px" }}>
          <div style={{ color: "#595959" }}>
            Show{" "}
            <Select
              size="small"
              value={unassignedFilterData.pageSize}
              onChange={(val) => unassignedOnShowSizeChange(1, val)}
              style={{ width: 65 }}
            >
              <Option value={5}>5</Option>
              <Option value={10}>10</Option>
              <Option value={20}>20</Option>
              <Option value={50}>50</Option>
            </Select>{" "}
            entries
          </div>
          <Pagination
            size="small"
            current={unassignedFilterData.currentPage}
            pageSize={unassignedFilterData.pageSize}
            total={unassignedItemsLength}
            onChange={unassignedOnPageChange}
            showSizeChanger={false}
          />
        </div>
      </Modal>
    </div>
  );
};

const Projects = () => {
  const classification = useSelector(
    (state) => state.orgdeatil.customerClassification
  );
  const { id } = useParams();
  const [expandedRowKey, setExpandedRowKey] = useState(null);
  const [expandedView, setExpandedView] = useState("edit"); // "edit" or "users"
  const [loadAgain, setLoadAgain] = useState(false);

  const {
    data: projects,
    itemsLength,
    loading,
    filterData,
    onShowSizeChange,
    onPageChange,
    handleSearchChange,
  } = useCommonListing({
    passSize: 5,
    passPage: 1,
    passURL: `/customer/projectslist/?customer_id=${id}`,
    includeStatus: false,
    loadAgain: loadAgain,
  });

  const columns = [
    {
      title: "Project ID",
      dataIndex: "project_id",
      key: "project_id",
      width: 120,
      fixed: "left",
    },
    {
      title: "Project Name",
      dataIndex: "project_name",
      key: "project_name",
      width: 220,
      fixed: "left",
      render: (text, record) => (
        <span
          style={{ color: "#1890ff", cursor: "pointer", fontWeight: 500 }}
          onClick={() => {
            setExpandedView("users");
            setExpandedRowKey((prev) => (prev === record.id && expandedView === "users" ? null : record.id));
          }}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Year",
      dataIndex: "project_year",
      key: "project_year",
      width: 90,
      align: "center",
    },
    {
      title: "JE Count",
      dataIndex: "total_ja_count",
      key: "total_ja_count",
      width: 100,
      align: "center",
      render: (count) => count ?? 0,
    },
    {
      title: "MP Count",
      dataIndex: "total_mp_count",
      key: "total_mp_count",
      width: 100,
      align: "center",
      render: (count) => count ?? 0,
    },
    {
      title: "Project Start",
      dataIndex: "start_date",
      key: "start_date",
      width: 120,
      align: "center",
      render: (date) => (date ? moment(date).format("MMM-DD-YYYY") : "-"),
    },
    {
      title: "Project End",
      dataIndex: "completed_date",
      key: "completed_date",
      width: 120,
      align: "center",
      render: (date) => (date ? moment(date).format("MMM-DD-YYYY") : "-"),
    },
    {
      title: "Access Start",
      dataIndex: "cust_access_start_date",
      key: "cust_access_start_date",
      width: 120,
      align: "center",
      render: (date) => (date ? moment(date).format("MMM-DD-YYYY") : "-"),
    },
    {
      title: "Access End",
      dataIndex: "cust_access_end_date",
      key: "cust_access_end_date",
      width: 120,
      align: "center",
      render: (date) => (date ? moment(date).format("MMM-DD-YYYY") : "-"),
    },
    {
      title: "Project Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      align: "center",
      render: (status) => {
        let tagColor = "default";
        let label = status ? String(status) : "-";
        
        const lowerStatus = String(status).toLowerCase();
        if (status === true || status === "True" || lowerStatus === "active" || status === 1) {
          tagColor = "success";
          label = "Active";
        } else if (status === false || status === "False" || lowerStatus === "inactive" || status === 0) {
          tagColor = "error";
          label = "Inactive";
        } else if (lowerStatus === "ongoing") {
          tagColor = "warning";
          label = "Ongoing";
        } else {
          tagColor = "default";
          label = status ? capitalizeWords(String(status)) : "-";
        }

        return (
          <Tag color={tagColor}>
            {label}
          </Tag>
        );
      },
    },
    {
      title: "Users",
      dataIndex: "total_user_count",
      key: "total_user_count",
      width: 90,
      align: "center",
      render: (count) => count ?? 0,
    },
    {
      title: "Login Access",
      dataIndex: "customer_access",
      key: "customer_access",
      width: 120,
      align: "center",
      render: (checked) => (
        checked ? (
          <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: "1.7rem" }} />
        ) : (
          <CloseCircleTwoTone twoToneColor="#ff4d4f" style={{ fontSize: "1.7rem" }} />
        )
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <div style={{ display: "flex", justifyContent: "center", gap: "12px", alignItems: "center" }}>
          <TeamOutlined
            style={{ fontSize: "1.2rem", color: "#1890ff", cursor: "pointer" }}
            title="View Users"
            onClick={() => {
              setExpandedView("users");
              setExpandedRowKey((prev) => (prev === record.id && expandedView === "users" ? null : record.id));
            }}
          />
          <EditTwoTone
            style={{ fontSize: "1.2rem", cursor: "pointer" }}
            title="Edit"
            onClick={() => {
              setExpandedView("edit");
              setExpandedRowKey((prev) => (prev === record.id && expandedView === "edit" ? null : record.id));
            }}
          />
         
        </div>
      ),
    },
  ];

  return (
    <Card style={{ width: "100%", marginBottom: "10px" }} hoverable>
      <Title level={5} style={{ marginInline: "10px" }}>
        Associated Projects Summary
      </Title>
      <CommonTopDetails
        title={`${classification ? capitalizeWords(classification) : "Customer"} Projects`}
        titleSuffix={`${itemsLength} project(s)`}
        searchPlaceholder="Search by..."
        searchAction={handleSearchChange}
        showAddButton={false}
        filterAction={false}
        marginPass="15px"
        paddingInlinepass="10px"
        topSizeAdd="5px"
        addButtonProps={{
          modalType: "PopUP",
          modalProps: () => {},
        }}
      />
      <div className="table-wrapper" style={{ overflowX: "auto" }}>
        <Table
          columns={columns}
          dataSource={projects}
          rowKey="id"
          size="small"
          loading={loading}
          className="table-container"
          style={{ margin: "1rem" }}
          pagination={false}
          scroll={{
            x: 1600,
          }}
          bordered
          expandable={{
            expandedRowRender: (record) => {
              if (expandedView === "edit") {
                return (
                  <ProjectEditRow
                    record={record}
                    onSuccess={() => {
                      setExpandedRowKey(null);
                      setLoadAgain((prev) => !prev);
                    }}
                    onCancel={() => setExpandedRowKey(null)}
                  />
                );
              } else if (expandedView === "users") {
                return (
                  <ProjectUsersRow
                    record={record}
                    customerId={id}
                  />
                );
              }
              return null;
            },
            expandedRowKeys: expandedRowKey ? [expandedRowKey] : [],
            showExpandColumn: false,
          }}
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          padding: "1rem",
        }}
      >
        <Pagination
          size="small"
          showSizeChanger
          onShowSizeChange={onShowSizeChange}
          onChange={onPageChange}
          pageSizeOptions={["5", "10", "15", "20", "50"]}
          current={filterData.currentPage}
          defaultPageSize={filterData.pageSize}
          total={itemsLength}
        />
      </div>
    </Card>
  );
};

export default Projects;

